import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FileBadge, FileClock, LayoutDashboard, LogOut, Menu, Plus, X } from "lucide-react";
import { isMenuItemActive, type MenuGroup, type NavBadge, type NavBadges } from "./navigation";

const badgeLabel = (badge: NavBadge) => (badge.count > 99 ? "99+" : String(badge.count));

function NavItem({ to, label, icon: Icon, active, badge, onNavigate }: { to: string; label: string; icon: MenuGroup["items"][number]["icon"]; active: boolean; badge?: NavBadge; onNavigate?: () => void }) {
  return <Link
    to={to}
    className={`cs-nav-link${active ? " active" : ""}`}
    aria-current={active ? "page" : undefined}
    aria-label={badge ? `${label}: ${badge.count}` : undefined}
    title={label}
    onClick={onNavigate}
  >
    <span className="cs-nav-icon"><Icon size={17} strokeWidth={active ? 2.25 : 1.75} aria-hidden="true"/>{badge && <i className={`cs-dot ${badge.tone}`} aria-hidden="true"/>}</span>
    <span className="cs-nav-label">{label}</span>
    {badge && <b className={`cs-badge ${badge.tone}`}>{badgeLabel(badge)}</b>}
  </Link>;
}

function NavGroups({ groups, pathname, badges, onNavigate }: { groups: MenuGroup[]; pathname: string; badges: NavBadges; onNavigate?: () => void }) {
  return <nav aria-label="Menu principal" className="cs-nav">
    {groups.map((group) => <div className="cs-nav-group" key={group.key}>
      <h3 className="cs-nav-heading">{group.label}</h3>
      <hr className="cs-nav-divider" aria-hidden="true"/>
      {group.items.map((item) => <NavItem key={item.to} {...item} active={isMenuItemActive(pathname, item.to)} badge={badges[item.to]} onNavigate={onNavigate}/>)}
    </div>)}
  </nav>;
}

/** Barra lateral do desktop (>= 1024 px): recolhida (76 px), abre ao passar o mouse por cima do conteúdo, como nos outros sistemas Meta. */
export function Sidebar({ groups, pathname, badges, onSignOut }: { groups: MenuGroup[]; pathname: string; badges: NavBadges; onSignOut: () => void }) {
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const schedule = (next: boolean) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(next), next ? 120 : 220);
  };
  useEffect(() => { if (timer.current) clearTimeout(timer.current); setOpen(false); }, [pathname]);

  return <div
    className="cs-sidebar-slot"
    onMouseEnter={() => schedule(true)}
    onMouseLeave={() => schedule(false)}
    onFocusCapture={() => setOpen(true)}
    onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false); }}
  >
    <aside className={`cs-sidebar ${open ? "is-open" : "is-compact"}`} aria-label="Navegação">
      <div className="cs-sidebar-glow" aria-hidden="true"/>
      <Link to="/" className="cs-brand" aria-label="Ir para o Dashboard">
        {open
          ? <img key="logo" className="cs-brand-logo" src="/assets/logo-meta-branca.png" alt="Meta Central de Serviços"/>
          : <img key="mark" className="cs-brand-mark" src="/assets/icon-white.png" alt="Meta Central de Serviços"/>}
      </Link>
      <NavGroups groups={groups} pathname={pathname} badges={badges}/>
      <div className="cs-sidebar-foot">
        <button type="button" className="cs-nav-link" onClick={onSignOut} aria-label="Sair da conta" title="Sair da conta">
          <span className="cs-nav-icon"><LogOut size={17} strokeWidth={1.75} aria-hidden="true"/></span>
          <span className="cs-nav-label">Sair</span>
        </button>
      </div>
    </aside>
  </div>;
}

/** Gaveta do celular/tablet (< 1024 px): mesmo menu, em painel flutuante. */
export function MobileDrawer({ open, onClose, groups, pathname, badges, onSignOut }: { open: boolean; onClose: () => void; groups: MenuGroup[]; pathname: string; badges: NavBadges; onSignOut: () => void }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = previous; };
  }, [open, onClose]);

  return <>
    <button type="button" className={`cs-drawer-scrim${open ? " open" : ""}`} onClick={onClose} aria-label="Fechar menu" tabIndex={open ? 0 : -1}/>
    <aside className={`cs-drawer${open ? " open" : ""}`} role="dialog" aria-modal="true" aria-label="Menu principal" aria-hidden={!open}>
      <div className="cs-sidebar-glow" aria-hidden="true"/>
      <div className="cs-drawer-head">
        <img className="cs-brand-logo" src="/assets/logo-meta-branca.png" alt="Meta Central de Serviços"/>
        <button type="button" className="cs-drawer-close" onClick={onClose} aria-label="Fechar menu"><X size={20} aria-hidden="true"/></button>
      </div>
      <NavGroups groups={groups} pathname={pathname} badges={badges} onNavigate={onClose}/>
      <div className="cs-sidebar-foot">
        <button type="button" className="cs-nav-link" onClick={() => { onClose(); onSignOut(); }}>
          <span className="cs-nav-icon"><LogOut size={18} strokeWidth={1.75} aria-hidden="true"/></span>
          <span className="cs-nav-label">Sair</span>
        </button>
      </div>
    </aside>
  </>;
}

type Tab = { to: string; label: string; icon: typeof LayoutDashboard };
const TABS_LEFT: Tab[] = [{ to: "/", label: "Início", icon: LayoutDashboard }, { to: "/vistorias", label: "Vistorias", icon: FileClock }];
const TABS_RIGHT: Tab[] = [{ to: "/documentos", label: "Documentos", icon: FileBadge }];

/** Barra inferior do celular/tablet: atalhos principais, botão central "Nova vistoria" e acesso ao menu completo. */
export function BottomNav({ pathname, canCreate, badges, menuBadge, onOpenMenu }: { pathname: string; canCreate: boolean; badges: NavBadges; menuBadge?: NavBadge; onOpenMenu: () => void }) {
  const tab = ({ to, label, icon: Icon }: Tab) => {
    const active = isMenuItemActive(pathname, to);
    const badge = badges[to];
    return <Link key={to} to={to} className={`cs-tab${active ? " active" : ""}`} aria-current={active ? "page" : undefined} aria-label={badge ? `${label}: ${badge.count}` : label}>
      <span className="cs-tab-icon"><Icon size={22} strokeWidth={active ? 2.25 : 1.75} aria-hidden="true"/>{badge && <i className={`cs-dot ${badge.tone}`} aria-hidden="true"/>}</span>
      <span>{label}</span>
    </Link>;
  };
  return <nav className="cs-bottomnav" aria-label="Navegação rápida">
    {TABS_LEFT.map(tab)}
    {canCreate && <div className="cs-tab-fab-slot"><Link to="/vistorias/nova" className="cs-fab" aria-label="Nova vistoria" title="Nova vistoria"><Plus size={26} strokeWidth={2.25} aria-hidden="true"/></Link></div>}
    {TABS_RIGHT.map(tab)}
    <button type="button" className="cs-tab" onClick={onOpenMenu} aria-label={menuBadge ? `Menu: ${menuBadge.count} aviso(s)` : "Abrir menu"}>
      <span className="cs-tab-icon"><Menu size={22} strokeWidth={1.75} aria-hidden="true"/>{menuBadge && <i className={`cs-dot ${menuBadge.tone}`} aria-hidden="true"/>}</span>
      <span>Menu</span>
    </button>
  </nav>;
}
